import { MapMarkerInput } from "./types";
import { html, getContrastingTextColor, safeIndex } from "./helpers";

// Types for marker generation
export interface PatientInfo {
  firstName?: string;
  lastName?: string;
}

export interface VisitEntry {
  scheduleIndex: number;
  time: string;
  displayName: string;
  fullLabel: string;
  timestamp: Date;
  colorIndex?: number;
  providerName?: string;
  patientId?: number;
}

export interface MarkerStyleConfig {
  backgroundColor?: string;
  textColor?: string;
  className?: string;
}

const markerColors = [
  "#3D1F47", "#FF5A4E", "#FF9E6D", "#0F9D58", "#AB47BC", "#00ACC1", "#FF7043", "#9E9E9E", "#5677FC", "#FF5722"
];

/**
 * Generates HTML for multi-visit patient markers
 */
export function generateMultiVisitPatientMarker(visitEntries: VisitEntry[] | MapMarkerInput['visitEntries'], colorIndex?: number): string {
  if (!visitEntries) return '';
  
  return `
    <div class="visit-list">
      ${visitEntries.map((visit, index) => {
        const visitColorIndex = safeIndex(visit.colorIndex || colorIndex, markerColors.length);
        const visitColor = visitColorIndex != null ? markerColors[visitColorIndex] : '#3D1F47';
        const patientInitials = visit.displayName.split(' ')
          .map(name => name.charAt(0).toUpperCase())
          .filter(char => char).join('');
          
        return html`
          <div class="visit-entry-hover" data-visit-index="${index}">
            <div class="patient-initials" style="background-color: ${visitColor}; color: ${getContrastingTextColor(visitColor)};">
              ${patientInitials}
            </div>
            <div class="patient-info">
              ${visit.displayName}
            </div>
          </div>
        `;
      }).join('')}
    </div>
  `;
}

/**
 * Generates HTML for multi-visit provider markers
 */
export function generateMultiVisitProviderMarker(visitEntries: VisitEntry[] | MapMarkerInput['visitEntries'], iconHtml: string): string {
  if (!visitEntries) return '';
  
  return `
    <div class="provider-content">
      <div class="marker-icon">${iconHtml}</div>
      <div class="visit-list">
        ${visitEntries.map((visit, index) => html`
          <div class="visit-entry-hover" 
               data-visit-index="${index}">
            ${visit.fullLabel}
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

/**
 * Generates HTML for single provider markers
 */
export function generateSingleProviderMarker(iconHtml: string, label: string): string {
  return `${iconHtml} ${html`${label}`}`;
}

/**
 * Determines if a marker should be highlighted based on visit timestamps
 */
export function hasHighlightedVisit(visitEntries?: MapMarkerInput['visitEntries']): boolean {
  if (!visitEntries) return false;
  return visitEntries.some(visit => visit.timestamp > new Date());
}

/**
 * Gets the appropriate CSS class for multi-visit patient markers
 */
export function getMultiPatientMarkerClass(visitEntries?: MapMarkerInput['visitEntries']): string {
  const isHighlighted = hasHighlightedVisit(visitEntries);
  return isHighlighted 
    ? "multi-patient-visit-marker"
    : "multi-patient-visit-marker multi-patient-visit-marker-old";
}

/**
 * Gets the appropriate CSS class for multi-visit provider markers
 */
export function getMultiProviderMarkerClass(visitEntries?: MapMarkerInput['visitEntries']): string {
  const isHighlighted = hasHighlightedVisit(visitEntries);
  return isHighlighted 
    ? "multi-provider-visit-marker"
    : "multi-provider-visit-marker multi-provider-visit-marker-old";
}

/**
 * Generates initials for single patient markers
 */
export function generateSinglePatientMarker(patient?: PatientInfo): string {
  if (!patient) return '';
  
  const initials = 
    (patient.firstName ? patient.firstName.charAt(0).toUpperCase() : "") +
    (patient.lastName ? patient.lastName.charAt(0).toUpperCase() : "");
  
  return initials;
}

/**
 * Applies styling to single patient markers
 */
export function applySinglePatientMarkerStyling(
  element: HTMLElement, 
  colorIndex?: number
): void {
  const safeColorIndex = safeIndex(colorIndex, markerColors.length);
  if (safeColorIndex != null) {
    const backgroundColor = markerColors[safeColorIndex];
    element.style.backgroundColor = backgroundColor;
    element.style.color = getContrastingTextColor(backgroundColor);
  }
  element.className = "dot-marker";
}

/**
 * Gets the appropriate CSS class for single provider markers
 */
export function getSingleProviderMarkerClass(highlighted: boolean): string {
  return highlighted
    ? "labeled-marker"
    : "labeled-marker labeled-marker-old";
}
